import { createRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useRegister } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth-store';
import { isApprovedBoundary } from '@/constants/role-policy';
import { rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: () => {
    const { isLoggedIn, user } = useAuthStore.getState();
    if (isLoggedIn && isApprovedBoundary(user?.roles)) {
      throw redirect({ to: '/' });
    }
  },
  component: RegisterPage,
});

function RegisterPage() {
  const registerMutation = useRegister();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (submitted && registerMutation.isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-10" data-ai-id="register-success-page">
        <div className="w-full max-w-md border border-[#ccc] bg-white p-8 text-center" data-ai-id="register-success-card">
          <p className="text-[11px] font-bold uppercase text-[#666]" data-ai-id="register-success-brand">
            CopyPaste
          </p>
          <h1 className="mt-3 text-xl font-bold text-[#333]" data-ai-id="register-success-title">
            Request submitted
          </h1>
          <p className="mt-3 text-[13px] leading-6 text-[#666]" data-ai-id="register-success-description">
            You can keep browsing now, and sign in to save favorites once your account is approved.
          </p>
          <Link
            to="/login"
            className="mt-6 inline-flex h-10 items-center justify-center border border-[#ccc] bg-[#f5f5f5] px-4 text-[13px] font-bold text-[#333] hover:bg-[#e6e6e6]"
            data-ai-id="register-success-login-link"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10" data-ai-id="register-page">
      <div className="w-full max-w-md border border-[#ccc] bg-white p-8" data-ai-id="register-page-card">
        <div className="mb-8 text-center" data-ai-id="register-page-header">
          <p className="text-[11px] font-bold uppercase text-[#666]" data-ai-id="register-page-brand">
            CopyPaste
          </p>
          <h1 className="mt-2 text-xl font-bold text-[#333]" data-ai-id="register-page-title">
            Request access
          </h1>
          <p className="mt-2 text-[13px] text-[#666]" data-ai-id="register-page-description">
            Create your account to request favorites access while continuing to browse as a guest.
          </p>
        </div>

        <form
          className="space-y-4"
          data-ai-id="register-form"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
            registerMutation.mutate({ name, email, password });
          }}
        >
          {registerMutation.error ? (
            <div className="border border-[#ebccd1] bg-[#f2dede] px-4 py-3 text-[13px] text-[#a94442]" data-ai-id="register-form-error">
              {registerMutation.error.message}
            </div>
          ) : null}

          <Input
            label="Full name"
            value={name}
            autoComplete="name"
            onChange={(event) => setName(event.target.value)}
            aiIdPrefix="register-form-name-field"
          />
          <Input
            label="Email"
            type="email"
            value={email}
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
            aiIdPrefix="register-form-email-field"
          />
          <Input
            label="Password"
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
            aiIdPrefix="register-form-password-field"
          />

          <Button
            className="w-full"
            loading={registerMutation.isPending}
            type="submit"
            data-ai-id="register-form-submit-button"
            aiIdPrefix="register-form-submit-button"
          >
            Submit request
          </Button>
        </form>

        <p className="mt-6 text-center text-[13px] text-[#666]" data-ai-id="register-page-login-prompt">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-[#0055cc] hover:underline" data-ai-id="register-page-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
